/**
 * UpdatePrompt — OTA 업데이트 안내 / 강제 게이트 / 스토어 이동 안내.
 *
 * otaStore 를 구독해 liveUpdate(네이티브 계층)가 띄운 상태를 렌더한다.
 * 기존 ConfirmDialog(portal+backdrop) 재사용. 설계: docs/84 §5.
 *
 * 마운트 위치: RootLayout(ToastProvider + Router 내부). useDialogBackBinding 의
 * 라우터 hook 과 useToast 가 동작하려면 반드시 라우터 트리 안이어야 한다.
 *
 * 적용 중(applying):
 *   - "업데이트 중입니다" 오버레이를 최상위로 띄워 모든 터치 차단.
 *   - useDialogBackBinding(blockClose) 로 하드웨어/브라우저 뒤로가기 흡수.
 *   - applyOtaBundle 성공 시 WebView reload 로 상태째 사라짐. 실패 시 applying 해제 + 토스트.
 */
import { useTranslation } from "react-i18next";
import { useOtaStore } from "../../store/otaStore";
import { applyOtaBundle, openAppStore } from "../../native/liveUpdate";
import { useDialogBackBinding } from "../../navigation/useDialogBackBinding";
import { useToast } from "../feedback/Toast";
import ConfirmDialog from "../feedback/ConfirmDialog";
import UpdatingOverlay from "./UpdatingOverlay";

function UpdatePrompt() {
  const { t } = useTranslation();
  /** 안내 본문은 릴리스 노트 전문 대신 버전 코드값만 노출. */
  const versionText = (version: string | null): string =>
    version ? t("update.version", { version }) : t("update.defaultNotes");

  const kind = useOtaStore((s) => s.kind);
  const bundleId = useOtaStore((s) => s.bundleId);
  const version = useOtaStore((s) => s.version);
  const applying = useOtaStore((s) => s.applying);
  const storeMandatory = useOtaStore((s) => s.storeMandatory);
  const storeMessage = useOtaStore((s) => s.storeMessage);
  const setApplying = useOtaStore((s) => s.setApplying);
  const dismiss = useOtaStore((s) => s.dismiss);
  const toast = useToast();

  // 뒤로가기 통합 — hook 규칙상 early return 전에 무조건 호출.
  //   prompt(미적용)/store(권장) → back = "나중에"(dismiss).
  //   mandatory/store(강제)/applying → back 흡수(닫히지 않음). 필수 게이트 백 탈출 차단.
  useDialogBackBinding({
    isOpen: kind !== "none",
    close: dismiss,
    kind: "ota-update",
    blockClose: applying || kind === "mandatory" || (kind === "store" && storeMandatory),
  });

  if (kind === "none") return null;

  // 적용 중엔 다이얼로그 대신 블로킹 오버레이만 렌더 (버튼이 뒤로 비치지 않게).
  if (applying) return <UpdatingOverlay />;

  const applyNow = async () => {
    if (!bundleId || applying) return;
    setApplying(true);
    // set() 성공 시 WebView reload → 이 컴포넌트째 새 번들로 다시 뜸.
    const ok = await applyOtaBundle(bundleId, version);
    if (!ok) {
      // 실패 → 오버레이를 풀고 안내. (applying 을 안 풀면 오버레이가 영구히 멈춘다.)
      setApplying(false);
      toast.error(t("update.applyFailed"));
    }
  };

  if (kind === "store") {
    const storeDescription = storeMessage ?? t("update.storeDefault");
    // 강제(차단) — backdrop/ESC/뒤로가기로 닫히지 않음. "나중에" 없음.
    if (storeMandatory) {
      return (
        <ConfirmDialog
          open
          hideSecondary
          title={t("update.requiredTitle")}
          description={storeDescription}
          primaryText={t("update.goStore")}
          onPrimaryPressed={() => void openAppStore()}
        />
      );
    }
    // 권장 — 닫기 가능.
    return (
      <ConfirmDialog
        open
        onClose={dismiss}
        title={t("update.storeTitle")}
        description={storeDescription}
        secondaryText={t("update.later")}
        primaryText={t("update.goStore")}
        onSecondaryPressed={dismiss}
        onPrimaryPressed={() => void openAppStore()}
      />
    );
  }

  if (kind === "mandatory") {
    return (
      <ConfirmDialog
        open
        // 강제 — backdrop/ESC 로 닫히지 않도록 onClose 미전달. 하드웨어 백은 blockClose 가 차단.
        hideSecondary
        title={t("update.requiredTitle")}
        description={versionText(version)}
        primaryText={t("update.now")}
        onPrimaryPressed={() => void applyNow()}
      />
    );
  }

  // kind === "prompt"
  return (
    <ConfirmDialog
      open
      onClose={dismiss}
      title={t("update.promptTitle")}
      description={versionText(version)}
      secondaryText={t("update.later")}
      primaryText={t("update.now")}
      onSecondaryPressed={dismiss}
      onPrimaryPressed={() => void applyNow()}
    />
  );
}

export default UpdatePrompt;
